/**
 * JSON-LD builders, shared by the React pages (src/components/JsonLd.jsx)
 * and the build-time prerenderer (scripts/prerender.mjs), so the structured
 * data in the static HTML and in the hydrated page is the same graph.
 *
 * Every page points at the one Organization and WebSite node by @id instead
 * of restating the company inline. That is what lets Google collapse hundreds
 * of pages into one entity rather than a thousand look-alike businesses.
 *
 * URLs carry the page language's prefix (/en/…), so an English page's
 * breadcrumb and offer URLs point at English pages.
 */

import { NAP, nap } from './nap.js';
import { LANGS, localizedPath } from './langs.js';
import { normaliseProject, projectAnswer, handoverLabel } from './projectSeo.js';
import { cityName, districtLabel } from './seoCopy.js';
import { gallery, bedroomLabel } from './projectContent.js';

const SITE = NAP.url;
export const ORG_ID = `${SITE}/#organization`;
export const WEBSITE_ID = `${SITE}/#website`;
export const orgRef = () => ({ '@id': ORG_ID });
const abs = (path, lang) => `${SITE}${localizedPath(path, lang)}`;

const ORG_DESCRIPTION = {
    ar: 'وسيط عقاري مرخّص في دبي متخصّص في مشاريع دبي وأبوظبي على الخارطة بأسعار المطوّر مباشرة وبدون عمولة على المشتري.',
    en: 'Licensed Dubai real estate broker specialising in Dubai and Abu Dhabi off-plan projects at direct developer prices with zero buyer commission.',
    ru: 'Лицензированный брокер в Дубае: строящиеся проекты Дубая и Абу-Даби по ценам застройщика без комиссии для покупателя.',
    zh: '迪拜持牌房产经纪，专营迪拜和阿布扎比期房项目，开发商直售价，买家零佣金。',
    ur: 'دبئی کا لائسنس یافتہ رئیل اسٹیٹ بروکر، دبئی اور ابوظہبی کے آف پلان پروجیکٹس براہِ راست ڈیولپر قیمت پر، خریدار سے کوئی کمیشن نہیں۔'
};
const LANGUAGE_NAMES = { ar: 'Arabic', en: 'English', ru: 'Russian', zh: 'Chinese', ur: 'Urdu' };

export function organizationNode(lang = 'ar') {
    const local = lang === 'ar' ? 'ar' : 'en';
    return {
        '@context': 'https://schema.org',
        '@type': ['RealEstateAgent', 'Organization'],
        '@id': ORG_ID,
        name: NAP.name[lang] || NAP.name.en,
        legalName: NAP.legalName,
        alternateName: NAP.alternateName,
        url: SITE,
        logo: { '@type': 'ImageObject', url: `${SITE}/logo.png`, width: 512, height: 512 },
        image: `${SITE}/logo.png`,
        description: ORG_DESCRIPTION[lang] || ORG_DESCRIPTION.en,
        telephone: NAP.phone,
        email: NAP.email,
        address: {
            '@type': 'PostalAddress',
            streetAddress: nap(NAP.address.street, local),
            addressLocality: nap(NAP.address.locality, local),
            addressRegion: nap(NAP.address.region, local),
            addressCountry: NAP.address.countryCode
        },
        geo: { '@type': 'GeoCoordinates', latitude: NAP.geo.lat, longitude: NAP.geo.lng },
        identifier: { '@type': 'PropertyValue', name: 'DLD Broker Number', value: NAP.dldBrokerNumber },
        hasCredential: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: 'Real Estate Broker Licence',
            identifier: NAP.dldBrokerNumber,
            recognizedBy: { '@type': 'GovernmentOrganization', name: nap(NAP.regulator, local) }
        },
        areaServed: NAP.areaServed.map((name) => ({ '@type': 'City', name })),
        openingHoursSpecification: NAP.openingHours.spec.map((s) => ({
            '@type': 'OpeningHoursSpecification', dayOfWeek: s.days, opens: s.opens, closes: s.closes
        })),
        sameAs: NAP.sameAs,
        contactPoint: [{
            '@type': 'ContactPoint',
            telephone: NAP.phone,
            contactType: 'sales',
            areaServed: ['AE', 'SA', 'KW', 'QA', 'OM', 'BH'],
            availableLanguage: LANGS.map((l) => LANGUAGE_NAMES[l])
        }],
        knowsAbout: [
            'Dubai off-plan property',
            'Abu Dhabi off-plan property',
            'Dubai real estate investment',
            'UAE property payment plans',
            'عقارات دبي على الخارطة',
            'الاستثمار العقاري في دبي'
        ]
    };
}

export function websiteNode(lang = 'ar') {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: NAP.name[lang] || NAP.name.en,
        url: SITE,
        inLanguage: LANGS,
        publisher: orgRef(),
        potentialAction: {
            '@type': 'SearchAction',
            target: { '@type': 'EntryPoint', urlTemplate: `${abs('/projects', lang)}?title={search_term_string}` },
            'query-input': 'required name=search_term_string'
        }
    };
}

/** items: [{ name, path }] — paths unprefixed; the language adds the prefix. */
export function breadcrumbNode(items, lang = 'ar') {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((it, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: it.name,
            item: abs(it.path, lang)
        }))
    };
}

/** url: the page's absolute URL; omitted for an FAQ nested in another page type. */
export function faqNode(items, url, lang = 'ar') {
    if (!items?.length) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        ...(url && { '@id': `${url}#faq`, url, publisher: orgRef() }),
        inLanguage: lang,
        mainEntity: items.map((it) => ({
            '@type': 'Question',
            name: it.q,
            acceptedAnswer: { '@type': 'Answer', text: it.a }
        }))
    };
}

const PROP = {
    handover: { ar: 'التسليم المتوقّع', en: 'Expected handover', ru: 'Ожидаемая сдача', zh: '预计交付', ur: 'متوقع حوالگی' },
    developer: { ar: 'المطوّر', en: 'Developer', ru: 'Застройщик', zh: '开发商', ur: 'ڈیولپر' },
    status: { ar: 'حالة المشروع', en: 'Project status', ru: 'Статус проекта', zh: '项目状态', ur: 'پروجیکٹ کی حیثیت' },
    category: { ar: 'نوع الوحدات', en: 'Unit type', ru: 'Тип юнитов', zh: '户型', ur: 'یونٹ کی قسم' }
};

/**
 * A project: Product (what makes `offers`, and so the price, legible to
 * Google's parsers) and Residence (the real estate semantics).
 *
 * Every property here belongs to one of those two types — Rich Results Test
 * and the schema.org validator both flag the ones that do not. Bedrooms and
 * sizes therefore sit on the unit types (accommodationFloorPlan → FloorPlan,
 * a Residence property), not on the project itself; the seller sits on the
 * offer, where schema.org defines it.
 */
export function projectNode(raw, lang = 'ar') {
    const p = normaliseProject(raw);
    const url = abs(`/project/${p.id}`, lang);
    const validUntil = new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10);
    const availability = p.soldOut ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock';
    const units = unitRowsForSchema(raw, lang);

    const offers =
        p.priceMin > 0
            ? p.priceMax > p.priceMin
                ? { '@type': 'AggregateOffer', lowPrice: p.priceMin, highPrice: p.priceMax, priceCurrency: 'AED', offerCount: Math.max(1, units.length), availability, priceValidUntil: validUntil, url, seller: orgRef() }
                : { '@type': 'Offer', price: p.priceMin, priceCurrency: 'AED', availability, priceValidUntil: validUntil, url, seller: orgRef() }
            : undefined;

    const [lat, lng] = String(p.latLong).split(',').map(Number);
    const additionalProperty = [
        p.handover && { '@type': 'PropertyValue', name: PROP.handover[lang], value: handoverLabel(p.handover, lang) },
        p.developer && { '@type': 'PropertyValue', name: PROP.developer[lang], value: p.developer },
        p.status && { '@type': 'PropertyValue', name: PROP.status[lang], value: p.status },
        p.category && { '@type': 'PropertyValue', name: PROP.category[lang], value: p.category }
    ].filter(Boolean);
    const images = gallery(raw, lang).map((g) => g.src).slice(0, 6);

    // A Product without offers fails the Product rich result outright ("offers,
    // review or aggregateRating required"). A project with no published price
    // — sold out, or not yet priced — is therefore a Residence only.
    const product = offers
        ? {
            sku: String(p.id),
            category: p.category || 'Residential Property',
            // Google's Product and merchant-listing checks expect brand to be
            // a Brand; an Organization there reads as "invalid object type".
            ...(p.developer && { brand: { '@type': 'Brand', name: p.developer } }),
            offers
        }
        : {};

    return {
        '@context': 'https://schema.org',
        '@type': offers ? ['Product', 'Residence'] : 'Residence',
        '@id': `${url}#project`,
        name: p.title,
        description: projectAnswer(raw, lang),
        url,
        ...(images.length && { image: images }),
        ...product,
        address: {
            '@type': 'PostalAddress',
            addressLocality: districtLabel(p.district) || cityName(p.city, 'en'),
            addressRegion: cityName(p.city, 'en'),
            addressCountry: 'AE'
        },
        ...(isFinite(lat) && isFinite(lng) && { geo: { '@type': 'GeoCoordinates', latitude: lat, longitude: lng } }),
        ...(units.length && { accommodationFloorPlan: units }),
        ...(additionalProperty.length && { additionalProperty })
    };
}

/** typical_units → FloorPlan nodes: bedrooms, size, layout image. */
function unitRowsForSchema(raw, lang) {
    const units = Array.isArray(raw.typical_units) ? raw.typical_units : [];
    const seen = new Set();
    return units
        .map((u) => {
            const beds = Number(u.bedroom);
            const lo = parseFloat(u.lowest_area), hi = parseFloat(u.highest_area);
            const name = bedroomLabel(u.bedroom, lang);
            const key = `${u.bedroom}|${lo}|${hi}`;
            if (seen.has(key)) return null;
            seen.add(key);
            return {
                '@type': 'FloorPlan',
                name,
                ...(isFinite(beds) && { numberOfBedrooms: beds }),
                ...(lo > 0 && {
                    floorSize: hi > lo
                        ? { '@type': 'QuantitativeValue', minValue: lo, maxValue: hi, unitCode: 'FTK' }
                        : { '@type': 'QuantitativeValue', value: lo, unitCode: 'FTK' }
                }),
                ...(Array.isArray(u.images) && u.images[0] && { layoutImage: u.images[0] })
            };
        })
        .filter(Boolean)
        .slice(0, 12);
}

/**
 * RealEstateListing describes the *page* as a listing, which is what Bing
 * and several AI crawlers key off, while Product/Residence describes the
 * *thing*.
 */
export function realEstateListingNode(raw, lang = 'ar') {
    const p = normaliseProject(raw);
    if (!p.id) return null;
    const url = abs(`/project/${p.id}`, lang);
    return {
        '@context': 'https://schema.org',
        '@type': 'RealEstateListing',
        '@id': `${url}#listing`,
        url,
        name: p.title,
        description: projectAnswer(raw, lang),
        ...(p.updatedAt && { datePosted: String(p.updatedAt).slice(0, 10) }),
        mainEntity: { '@id': `${url}#project` },
        provider: orgRef(),
        inLanguage: lang
    };
}

/** A blog post. path is unprefixed; the post's language adds the prefix. */
export function articleNode({ title, description, image, path, publishedAt, updatedAt, lang }) {
    const img = image ? (String(image).startsWith('http') ? image : `${SITE}${image}`) : `${SITE}/logo.png`;
    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        image: [img],
        author: orgRef(),
        publisher: orgRef(),
        datePublished: publishedAt,
        dateModified: updatedAt || publishedAt,
        mainEntityOfPage: { '@type': 'WebPage', '@id': abs(path, lang) },
        inLanguage: lang
    };
}

/** CollectionPage + ItemList for a hub page (area, developer). */
export function collectionNode({ url, name, description, lang, about, items }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${url}#page`,
        url,
        name,
        description,
        inLanguage: lang,
        isPartOf: { '@id': `${SITE}/#website` },
        ...(about && { about }),
        publisher: orgRef(),
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: items.length,
            itemListElement: items.slice(0, 30).map((p, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: abs(`/project/${p.id}`, lang),
                name: p.title
            }))
        }
    };
}
